import { datadogRum } from "@datadog/browser-rum";
import { DocumentMagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { ArrowDownTrayIcon, ArrowPathIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  FileStatusType,
  FileUploadAndParseProps,
  Label,
  Select,
  SelectItem,
  humanFileSize,
} from "@fronterahealth/frontera-ui-components";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@fronterahealth/frontera-ui-components/ui/combobox";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@fronterahealth/frontera-ui-components/ui/item";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileProcessingFailureCodeChoices,
  ApiReportReportTypeChoices,
  useStartLearnerFileValidationMutation,
  useUpdateLearnerFileClassificationMutation,
  useUpdateLearnerFileInterviewTypeMutation,
  useUpdateLearnerFileLanguageMutation,
} from "@api/graphql/types-and-hooks";
import { HighlightedText } from "@components/HighlightedText/HighlightedText";
import { FileStatusDisplay } from "@components/UploadDocuments/FileStatusDisplay";
import { AssessmentSelectItem, isFileTypeSupportedForAssessment } from "@components/UploadDocuments/fileTypeSupport";
import {
  InterviewType,
  interviewTypeFromMetadata,
  makeInterviewOptionId,
  parseInterviewType,
  toSelectableInterviewType,
} from "@components/UploadDocuments/interviewType";
import { isFileUploaded as isFileUploadedHelper } from "@components/UploadDocuments/isFileUploaded";
import {
  TRANSCRIBE_LANGUAGE_OPTIONS,
  getDefaultTranscribeLanguage,
  isTranscribeMime,
  languageFromMetadata,
} from "@components/UploadDocuments/transcribeLanguage";
import { notifyError } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";
import { useAnalytics } from "@providers/AnalyticsProvider";

const ASSESSMENT = "ASSESSMENT";

interface RunInterviewTypeChangeOptions {
  selected: SelectItem;
  type: InterviewType;
  fileKind?: string;
  fileId: string;
  fileName: string;
  currentInterviewType: InterviewType;
  updateInterviewType: {
    isPending: boolean;
    mutateAsync: (vars: { fileId: string; interviewType: string }) => Promise<unknown>;
  };
  updateLearnerFileClassification: (newFileKind: string) => Promise<boolean>;
  setSelectedDocumentType: (value: SelectItem) => void;
  setSelectedAssessmentType: (value: AssessmentSelectItem | null) => void;
  pendingAssessmentSelection: React.MutableRefObject<boolean>;
  pendingInterviewSelection: React.MutableRefObject<boolean>;
  analytics: ReturnType<typeof useAnalytics>;
  reportId?: string;
  reportType?: ApiReportReportTypeChoices;
  orgId?: string;
  orgName?: string;
  onFileUpdated?: (fileName: string, fileId: string) => void;
  autoSetDocumentType: () => void;
}

// Extracted from FileTableRow to keep the component's cognitive complexity in check.
// Saves the interview respondent, reclassifying first if the file wasn't already an
// interview, and captures override telemetry only for a genuine correction.
const runInterviewTypeChange = async ({
  selected,
  type,
  fileKind,
  fileId,
  fileName,
  currentInterviewType,
  updateInterviewType,
  updateLearnerFileClassification,
  setSelectedDocumentType,
  setSelectedAssessmentType,
  pendingAssessmentSelection,
  pendingInterviewSelection,
  analytics,
  reportId,
  reportType,
  orgId,
  orgName,
  onFileUpdated,
  autoSetDocumentType,
}: RunInterviewTypeChangeOptions): Promise<void> => {
  // Don't overlap saves; a returning poll/second pick could otherwise land out of order.
  if (updateInterviewType.isPending) return;
  const alreadyInterview = fileKind === ApiLearnerFileFileKindChoices.Interview;
  // No-op when the respondent is unchanged on an existing interview: skip the network
  // call (and its telemetry), just keep the dropdown on the selected option.
  if (alreadyInterview && type === currentInterviewType) {
    setSelectedDocumentType(selected);
    setSelectedAssessmentType(null);
    return;
  }
  pendingAssessmentSelection.current = false;
  pendingInterviewSelection.current = true;
  const previousType = currentInterviewType;
  // Switching INTO interview from another kind reclassifies (+ reparses); track it so a
  // mid-flight failure can tell the user the file was already recategorized.
  const reclassifiedToInterview = !alreadyInterview;
  try {
    if (reclassifiedToInterview) {
      const success = await updateLearnerFileClassification(ApiLearnerFileFileKindChoices.Interview);
      if (!success) return;
    }
    await updateInterviewType.mutateAsync({ fileId, interviewType: type });
    setSelectedDocumentType(selected);
    setSelectedAssessmentType(null);
    // Telemetry: capture only a genuine correction on an already-classified interview.
    // A reclassify INTO interview is an initial pick, not an override, and would skew
    // the inference-accuracy metric (previousType defaults to "other").
    if (!reclassifiedToInterview && previousType !== type) {
      analytics?.capture("interview_type_overridden", {
        file_id: fileId,
        report_id: reportId,
        report_type: reportType,
        org_id: orgId,
        org_name: orgName,
        inferred_type: previousType,
        chosen_type: type,
      });
    }
    onFileUpdated?.(fileName, fileId);
  } catch {
    notifyError(
      reclassifiedToInterview
        ? "File was set to Interview, but saving the interview source failed. Please re-select it."
        : "Failed to update interview source",
    );
    autoSetDocumentType();
  } finally {
    pendingInterviewSelection.current = false;
  }
};

// Placeholder for the Document Type select. Extracted from the component's JSX to keep
// its cognitive complexity under the limit.
const documentTypePlaceholder = (fileStatus: FileStatusType, fileKind?: string): string => {
  if (fileStatus === "uploading") return "Uploading...";
  if (fileKind === ApiLearnerFileFileKindChoices.Unclassified && fileStatus === "classifying") {
    return "Identifying...";
  }
  return "Select a category";
};

interface FileTableRowProps {
  className?: string;
  fileName: string;
  fileId: string;
  fileSize: number;
  fileStatus: FileStatusType;
  failureReason?: string;
  failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
  fileKind?: string;
  mimeType?: string;
  metadata?: string | null;
  removeFileCallback: FileUploadAndParseProps["removeFileCallback"];
  downloadFileCallback?: FileUploadAndParseProps["downloadFileCallback"];
  previewFileCallback?: (fileId: string) => Promise<void>;
  resetFile: () => void;
  allowFileDownload?: boolean;
  assessmentTypeOptions: AssessmentSelectItem[];
  documentTypeOptions: SelectItem[];
  onFileUpdated?: (fileName: string, fileId: string) => void;
  // Context for the manual-override Datadog signal. Optional so existing tests
  // and any future consumers can omit them; the event simply drops missing keys.
  reportId?: string;
  reportType?: ApiReportReportTypeChoices;
  learnerId?: string;
}

export const FileTableRow: React.FC<FileTableRowProps> = ({
  className,
  fileName,
  fileSize,
  fileStatus,
  failureReason,
  failureCode,
  fileId,
  fileKind,
  mimeType,
  metadata,
  resetFile,
  removeFileCallback,
  downloadFileCallback,
  previewFileCallback,
  allowFileDownload,
  assessmentTypeOptions,
  documentTypeOptions,
  onFileUpdated,
  reportId,
  reportType,
  learnerId,
}) => {
  const [fileGettingRemoved, setFileGettingRemoved] = useState<boolean>(false);
  const [fileGettingDownload, setFileGettingDownload] = useState<boolean>(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<SelectItem | undefined>();
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<AssessmentSelectItem | null>(null);
  const [assessmentQuery, setAssessmentQuery] = useState<string>("");
  const [isUpdatingClassification, setIsUpdatingClassification] = useState<boolean>(false);
  // True while the user has picked "Assessment" from the doc-type dropdown but hasn't yet
  // chosen a specific assessment subtype. Prevents poll updates from stomping the pending selection.
  const pendingAssessmentSelection = useRef<boolean>(false);
  // True while an interview-type selection is being saved. Without this, a status poll
  // returning stale metadata can revert the user's just-made respondent pick.
  const pendingInterviewSelection = useRef<boolean>(false);

  const updateClassification = useUpdateLearnerFileClassificationMutation();
  const startParsing = useStartLearnerFileValidationMutation();
  const updateLanguage = useUpdateLearnerFileLanguageMutation();
  const updateInterviewType = useUpdateLearnerFileInterviewTypeMutation();
  const analytics = useAnalytics();

  const { isKuwaitUser, roles, orgId, orgName } = useAdminData();

  // Prefer the user's pick (mutation variables) over the polled metadata so a refresh
  // doesn't flicker the dropdown back to the backend-inferred value. We intentionally do
  // NOT gate this on isPending: React Query keeps `variables` after success, which bridges
  // the gap until the next poll reports the saved value (gating on isPending would
  // reintroduce that revert flicker). `variables` is per-row (the hook is per-row), and
  // once the mutation succeeds the optimistic value equals what the server will report.
  const optimisticInterviewType =
    !updateInterviewType.isError && updateInterviewType.variables?.interviewType
      ? (updateInterviewType.variables.interviewType as InterviewType)
      : undefined;
  const currentInterviewType: InterviewType = optimisticInterviewType ?? interviewTypeFromMetadata(metadata);
  // What the picker shows: child/teacher (hidden) collapse to Other. Used for both display
  // and the change/telemetry comparison so re-picking the shown value is a true no-op.
  const displayInterviewType: InterviewType = toSelectableInterviewType(currentInterviewType);

  const isAssessmentSelected = selectedDocumentType?.id === ASSESSMENT;
  const isFileUploaded = isFileUploadedHelper(fileStatus);
  const isTranscribable = isTranscribeMime(mimeType);

  // Fire once per file when the template-PDF failure surfaces. No PII / no file names.
  const templatePdfEventFiredRef = useRef(false);
  const isTemplatePdfFailure =
    fileStatus === "parsing-failed" && failureCode === ApiLearnerFileProcessingFailureCodeChoices.FronteraTemplatePdf;
  useEffect(() => {
    if (isTemplatePdfFailure && !templatePdfEventFiredRef.current) {
      templatePdfEventFiredRef.current = true;
      analytics?.capture("file_processing_failed", {
        error_type: "frontera_template_pdf",
        document_type: fileKind,
        file_extension: "pdf",
      });
    }
    if (!isTemplatePdfFailure) {
      templatePdfEventFiredRef.current = false;
    }
  }, [isTemplatePdfFailure, fileKind, analytics]);

  const propsLanguage = languageFromMetadata(metadata);
  // Prefer the latest user pick (mutation `variables`) until/unless it failed; otherwise
  // fall back to whatever the parent query last reported, then to the org default.
  const optimisticLanguage = updateLanguage.isError ? undefined : updateLanguage.variables?.language;
  const currentLanguage = optimisticLanguage ?? propsLanguage ?? getDefaultTranscribeLanguage(isKuwaitUser);
  const selectedLanguage = TRANSCRIBE_LANGUAGE_OPTIONS.find((opt) => opt.id === currentLanguage) ?? null;

  const handleLanguageChange = async (selected: SelectItem) => {
    if (!selected?.id || !fileId || updateLanguage.isPending) return;
    if (selected.id === currentLanguage) return;
    try {
      await updateLanguage.mutateAsync({ fileId, language: String(selected.id) });
      onFileUpdated?.(fileName, fileId);
    } catch {
      notifyError("Failed to update language");
    }
  };

  const autoSetDocumentType = useCallback(() => {
    if (pendingAssessmentSelection.current || pendingInterviewSelection.current) return;

    if (fileKind === ApiLearnerFileFileKindChoices.Unclassified) {
      return;
    }

    // Interview files: when composite "Interview - X" options are present (flag on), map
    // (INTERVIEW + interview_type) onto the matching one; otherwise fall back to the single
    // generic "Interview" option (flag off). Absent metadata maps to Other.
    if (fileKind === ApiLearnerFileFileKindChoices.Interview) {
      const interviewOption =
        documentTypeOptions.find((opt) => opt.id === makeInterviewOptionId(displayInterviewType)) ??
        documentTypeOptions.find((opt) => opt.id === ApiLearnerFileFileKindChoices.Interview);
      if (interviewOption) {
        setSelectedDocumentType(interviewOption);
        setSelectedAssessmentType(null);
      }
      return;
    }

    // If its an assessment type
    const assessmentOption = assessmentTypeOptions.find((opt) => opt.id === fileKind);
    if (assessmentOption) {
      setSelectedDocumentType(documentTypeOptions.find((opt) => opt.id === ASSESSMENT));
      setSelectedAssessmentType(assessmentOption);
      return;
    }

    // It's a non-assessment type
    const documentOption = documentTypeOptions.find((opt) => opt.id === fileKind);
    if (documentOption) {
      setSelectedDocumentType(documentOption);
      setSelectedAssessmentType(null);
    }
  }, [fileKind, assessmentTypeOptions, documentTypeOptions, displayInterviewType]);

  // Auto-select document type when classification completes
  useEffect(() => {
    autoSetDocumentType();
  }, [autoSetDocumentType, fileStatus]);

  const handleDownload = async () => {
    if (!downloadFileCallback) {
      console.error("no download callback provided");
      return;
    }
    setFileGettingDownload(true);
    await downloadFileCallback(fileId);
    setFileGettingDownload(false);
  };

  const updateLearnerFileClassification = async (newFileKind: string): Promise<boolean> => {
    if (!newFileKind || isUpdatingClassification) return false;

    // Snapshot the prior classification before the mutation flips it; polling can
    // otherwise stomp `fileKind` mid-flight and we'd lose the signal.
    const previousClassification = fileKind;

    try {
      setIsUpdatingClassification(true);

      // Step 1: Update classification
      await updateClassification.mutateAsync({
        fileId,
        classification: newFileKind,
      });

      // Step 2: Start validation/parsing
      await startParsing.mutateAsync({
        learnerFileId: fileId,
      });

      // Step 3: Callback for the specific file
      if (onFileUpdated) {
        onFileUpdated(fileName, fileId);
      }

      // Telemetry: a real user override (not a no-op, not a first-classification from
      // unclassified). Drives a Datadog dashboard of which kinds get re-categorized
      // most often, to prioritize auto-classification fixes.
      const isRealOverride =
        previousClassification &&
        previousClassification !== ApiLearnerFileFileKindChoices.Unclassified &&
        previousClassification !== newFileKind;
      if (isRealOverride) {
        datadogRum.addAction("file-classification-manually-overridden", {
          fileMetadata: {
            fileId,
            fileName,
            previousClassification,
            newClassification: newFileKind,
            reportId,
            reportType,
            learnerId,
            userRoles: roles,
          },
        });
      }

      return true;
    } catch (_error) {
      notifyError("Failed to update file kind");
      return false;
    } finally {
      setIsUpdatingClassification(false);
    }
  };

  // Interviews fold the respondent into the Document Type dropdown as composite
  // "Interview - X" options, rather than the two-tier pattern assessments use (pick
  // "Assessment", then a subtype in Document Properties). This is deliberate: the
  // Document Properties column already hosts the audio/video language picker, and
  // interviews are usually audio/video, so a second picker there would collide.
  // Logic lives in runInterviewTypeChange (module scope) to keep this component's
  // cognitive complexity down.
  const handleInterviewTypeChange = (selected: SelectItem, type: InterviewType) =>
    runInterviewTypeChange({
      selected,
      type,
      fileKind,
      fileId,
      fileName,
      currentInterviewType: displayInterviewType,
      updateInterviewType,
      updateLearnerFileClassification,
      setSelectedDocumentType,
      setSelectedAssessmentType,
      pendingAssessmentSelection,
      pendingInterviewSelection,
      analytics,
      reportId,
      reportType,
      orgId,
      orgName,
      onFileUpdated,
      autoSetDocumentType,
    });

  const handleDocumentTypeChange = async (selected: SelectItem) => {
    // Composite "Interview - X" options carry both the kind and the respondent.
    const interviewType = parseInterviewType(selected.id);
    if (interviewType) {
      await handleInterviewTypeChange(selected, interviewType);
      return;
    }
    // If not Assessment, update classification first
    if (selected.id !== ASSESSMENT) {
      pendingAssessmentSelection.current = false;
      const success = await updateLearnerFileClassification(selected.id as string);
      // Only update UI state if classification update succeeded
      if (success) {
        setSelectedDocumentType(selected);
        setSelectedAssessmentType(null);
      }
    } else {
      // For Assessment type, just update the UI immediately (no API call yet).
      // Guard against poll resets until the user picks an assessment subtype.
      pendingAssessmentSelection.current = true;
      setSelectedDocumentType(selected);
    }
  };

  const handleAssessmentTypeChange = async (selected: AssessmentSelectItem) => {
    // Update classification first
    const success = await updateLearnerFileClassification(selected.id as string);
    pendingAssessmentSelection.current = false;
    if (success) {
      setSelectedAssessmentType(selected);
    } else {
      // The useEffect won't re-fire on its own if fileKind/fileStatus haven't
      // changed (e.g. polling already stopped at a terminal state), so replay
      // the sync manually to reset the dropdowns to the current backend value.
      autoSetDocumentType();
    }
  };

  const classificationError = fileStatus === "classifying-error";

  if (!fileName) return null;

  return (
    <div
      className={twMerge("grid gap-4 items-center p-2 border-b border-border-medium animate-in-fade-top", className)}
    >
      {/* File Name Column */}
      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
        <DocumentTextIcon className="h-5 w-5 text-icon-tertiary shrink-0" />
        <Label displayType="loud" size="small" colorType="primary" className="truncate ph-no-capture">
          {fileName}
        </Label>
      </div>
      {/* File Size Column */}
      <div className="min-w-0 text-right">
        <Label size="small" className="text-text-tertiary truncate">
          {humanFileSize(fileSize)}
        </Label>
      </div>
      {/* Status Column */}
      <div className="flex flex-col items-start justify-center gap-2">
        <FileStatusDisplay fileStatus={fileStatus} failureReason={failureReason} failureCode={failureCode} />
      </div>
      {/* Document Type Column */}
      <div className="min-w-0">
        <Select
          title=""
          items={documentTypeOptions}
          selected={selectedDocumentType}
          setSelected={handleDocumentTypeChange}
          placeholderText={documentTypePlaceholder(fileStatus, fileKind)}
          inputClassName={classificationError ? "border border-red-500" : ""}
          formNoValidate={true}
          truncatePrimary={false}
          disabled={fileStatus === "uploading"}
        />
      </div>
      {/* Document Properties Column */}
      <div className={twMerge("min-w-0")}>
        {isTranscribable && (
          <Select
            title=""
            items={TRANSCRIBE_LANGUAGE_OPTIONS}
            selected={selectedLanguage}
            setSelected={handleLanguageChange}
            placeholderText="Select language"
            formNoValidate={true}
            // Document Type must be confirmed as Interview -- the only type this flow
            // accepts -- before language becomes pickable. Keeps selection order linear:
            // classify Document Type first, then language. See PROA-1659.
            disabled={
              updateLanguage.isPending ||
              fileStatus === "uploading" ||
              fileKind !== ApiLearnerFileFileKindChoices.Interview
            }
          />
        )}
        {!isTranscribable && isAssessmentSelected && (
          <Combobox<AssessmentSelectItem>
            items={assessmentTypeOptions}
            itemToStringLabel={(item) => item.primary}
            isItemEqualToValue={(a, b) => a.id === b.id}
            value={selectedAssessmentType}
            autoHighlight
            onValueChange={(next) => next && handleAssessmentTypeChange(next)}
            onInputValueChange={(query) => setAssessmentQuery(query)}
            filter={(item, query) =>
              !!item.primary.toLowerCase().includes(query.toLowerCase()) ||
              !!item.secondary?.toLowerCase().includes(query.toLowerCase())
            }
          >
            <ComboboxInput
              placeholder="Select assessment"
              aria-invalid={
                !!selectedAssessmentType && !isFileTypeSupportedForAssessment(fileName, selectedAssessmentType)
              }
            />
            <ComboboxContent side="bottom">
              <ComboboxEmpty>No assessment type.</ComboboxEmpty>
              <ComboboxList>
                {(item: AssessmentSelectItem) => (
                  <ComboboxItem key={item.id} value={item}>
                    <Item size="xs" className="p-0">
                      <ItemContent>
                        <ItemTitle>
                          <span>
                            <HighlightedText text={item.primary} highlightTerm={assessmentQuery} />
                          </span>
                        </ItemTitle>
                        {item.secondary && (
                          <ItemDescription className="text-muted-foreground text-pretty">
                            <HighlightedText text={item.secondary} highlightTerm={assessmentQuery} />
                          </ItemDescription>
                        )}
                      </ItemContent>
                    </Item>
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )}
      </div>
      {/* Actions Column */}
      <div className="flex items-center justify-end gap-2">
        {allowFileDownload && isFileUploaded && previewFileCallback && (
          <button
            onClick={() => previewFileCallback(fileId)}
            className="text-text-primary hover:text-text-secondary animate-in-fade-left"
            data-testid="preview-file-button"
          >
            <DocumentMagnifyingGlassIcon className="h-5 w-5 cursor-pointer" />
          </button>
        )}
        {allowFileDownload && isFileUploaded && (
          <button
            onClick={handleDownload}
            className="text-text-primary hover:text-text-secondary animate-in-fade-left"
            data-testid="download-file-button"
          >
            {fileGettingDownload ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin-slow text-text-secondary" />
            ) : (
              <ArrowDownTrayIcon className="h-5 w-5 cursor-pointer" />
            )}
          </button>
        )}
        <XMarkIcon
          data-testid="remove-file-button"
          onClick={async () => {
            if (fileStatus === "uploading" || fileGettingRemoved) return;
            setFileGettingRemoved(true);
            try {
              if (fileStatus !== "uploading-failed-file-size-exceed" && fileId) {
                await removeFileCallback(fileId);
              }
              resetFile();
            } catch (error) {
              console.error("Failed to remove file:", error);
            } finally {
              setFileGettingRemoved(false);
            }
          }}
          className={`h-5 w-5 ${fileGettingRemoved ? "animate-spin-slow" : ""} ${!(fileStatus === "uploading" || fileGettingRemoved) ? "cursor-pointer text-text-primary hover:text-text-secondary" : "pointer-events-none cursor-not-allowed text-limestone-200"}`}
          aria-disabled={fileStatus === "uploading" || fileGettingRemoved}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};
